'use client'

import React, { useRef, useEffect, useState } from 'react'
import { Pill } from './Pill'
import { Prism } from './Prism'

interface RayPath {
  x1: number
  y1: number
  x2: number
  y2: number
}

interface PillsToPrismProps {
  pills: readonly string[]
}

export function PillsToPrism({ pills }: PillsToPrismProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const pillRefs = useRef<(HTMLDivElement | null)[]>([])
  const prismRef = useRef<HTMLDivElement>(null)
  const [rayPaths, setRayPaths] = useState<RayPath[]>([])
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const updateRayPaths = () => {
      if (!containerRef.current || !prismRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()
      const prismRect = prismRef.current.getBoundingClientRect()

      // Calculate convergence point (center of the prism)
      const prismInnerContainer = prismRef.current?.querySelector('svg') as SVGSVGElement | null
      
      let convergenceX = prismRect.left + prismRect.width / 2 - containerRect.left
      let convergenceY = prismRect.top + prismRect.height / 2 - containerRect.top
      
      if (prismInnerContainer) {
        const svgRect = prismInnerContainer.getBoundingClientRect()
        convergenceX = svgRect.left + svgRect.width / 2 - containerRect.left
        convergenceY = svgRect.top + svgRect.height / 2 - containerRect.top
      }
      
      const newPaths: RayPath[] = []

      pillRefs.current.forEach((pillWrapper) => {
        if (!pillWrapper) return

        // Get the actual pill element (first child is the pill div)
        const pillElement = pillWrapper.firstChild as HTMLElement
        if (!pillElement) return

        const pillRect = pillElement.getBoundingClientRect()
        
        // Get right edge of the pill element (this is the actual visual edge)
        const x1 = pillRect.right - containerRect.left
        const y1 = pillRect.top + pillRect.height / 2 - containerRect.top

        // All rays converge to the same point
        const x2 = convergenceX
        const y2 = convergenceY

        newPaths.push({ x1, y1, x2, y2 })
      })

      setRayPaths(newPaths)
    }

    updateRayPaths()
    
    const timeoutId = setTimeout(updateRayPaths, 100)

    // Recompute on window resize/scroll
    window.addEventListener('resize', updateRayPaths)
    window.addEventListener('scroll', updateRayPaths, { passive: true })

    // Observe size changes
    const containerRO = new ResizeObserver(updateRayPaths)
    const prismRO = new ResizeObserver(updateRayPaths)
    if (containerRef.current) containerRO.observe(containerRef.current)
    if (prismRef.current) prismRO.observe(prismRef.current)

    // Observe style/attribute changes (e.g., top/left updates) on prism
    const prismMO = new MutationObserver(updateRayPaths)
    if (prismRef.current) prismMO.observe(prismRef.current, { attributes: true, attributeFilter: ['style', 'class'] })

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', updateRayPaths)
      window.removeEventListener('scroll', updateRayPaths)
      containerRO.disconnect()
      prismRO.disconnect()
      prismMO.disconnect()
    }
  }, [pills])

  // Intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect() // Only trigger once
        }
      },
      { threshold: 0.3 } // Trigger when 30% visible
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} className="relative w-full h-full" style={{ minHeight: '600px' }}>
      {/* Pills positioned on the left - in front of rays */}
      <div className="absolute left-0 top-0 flex flex-col gap-4" style={{ top: '10px', zIndex: 1 }}>
        {pills.map((pill, index) => (
          <div 
            key={index} 
            ref={(el) => {
              pillRefs.current[index] = el
            }}
            style={{ 
              marginLeft: index % 2 === 0 ? '0' : '20px',
              width: '220px',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
              transition: `opacity 0.6s ease-out ${0.2 + index * 0.2}s, transform 0.6s ease-out ${0.2 + index * 0.2}s`
            }}
          >
            <Pill size="sm">{pill}</Pill>
          </div>
        ))}
      </div>

      {/* Rays - behind pills with taper effect */}
      <svg 
        className="absolute inset-0 pointer-events-none" 
        style={{ 
          overflow: 'visible', 
          zIndex: 0,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.8s ease-out 4s'
        }}
      >
        <defs>
          <linearGradient id="rayGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#88C7DB" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#FAF9F5" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {rayPaths.map((path, index) => (
          <line
            key={index}
            x1={path.x1}
            y1={path.y1}
            x2={path.x2}
            y2={path.y2}
            stroke="url(#rayGradient)"
            strokeWidth="5"
            strokeLinecap="round"
          />
        ))}
      </svg>

      {/* Prism positioned on the right - higher z-index to be in foreground */}
      <div 
        id="prism-root" 
        className="absolute z-10" 
        ref={prismRef} 
        style={{ 
          top: '40%', 
          right: '8%',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.6s ease-out 1.2s, transform 0.6s ease-out 1.2s'
        }}
      >
        <Prism size={200} />
      </div>
    </div>
  )
}
